import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getJobBySlug } from '@/lib/contentful';
import type { JobPosting } from '@/types/job';
import ApplicationForm from '@/components/ApplicationForm';

function ApplyPage() {
  const { slug } = useParams<{ slug: string }>();
  const [job, setJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchJob() {
      if (!slug) return;
      setLoading(true);
      const data = await getJobBySlug(slug);
      setJob(data);
      setLoading(false);
    }
    fetchJob();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-16">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-8 w-64 rounded bg-gray-200" />
            <div className="h-4 w-48 rounded bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-16 text-center">
          <h1 className="font-heading text-2xl font-semibold text-heading">
            Position Not Found
          </h1>
          <p className="mt-3 font-body text-body">
            This job posting may have been removed or the link may be incorrect.
          </p>
          <Link to="/" className="btn-primary mt-6 inline-flex">
            View All Positions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Apply for {job.title} | ISHI Health Careers</title>
        <meta
          name="description"
          content={`Apply for the ${job.title} position at ISHI Health.`}
        />
      </Helmet>

      {/* Header */}
      <section className="bg-brand-purple-dark">
        <div className="mx-auto max-w-content px-6 py-12">
          <Link
            to={`/jobs/${job.slug}`}
            className="inline-flex items-center gap-1.5 font-body text-sm text-white/60 transition-colors hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to {job.title}
          </Link>

          <h1 className="mt-4 font-heading text-3xl font-semibold text-white md:text-4xl">
            Apply for {job.title}
          </h1>
          <p className="mt-2 font-body text-sm text-white/60">
            {job.department} &middot; {job.location} &middot; {job.type}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-12 md:py-16">
          <div className="mx-auto max-w-2xl">
            <div className="rounded-xl border border-gray-100 bg-white p-8">
              <h2 className="mb-2 font-heading text-xl font-semibold text-heading">
                Your Information
              </h2>
              <p className="mb-8 font-body text-sm text-muted">
                Fields marked with <span className="text-red-500">*</span> are required.
              </p>

              <ApplicationForm jobTitle={job.title} department={job.department} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ApplyPage;
